// Canonical grading company / grade / card-type constants used across the app.
//
// The set of *storage* codes is intentionally frozen — old rows keep their
// existing value verbatim; new rows use one of these codes. `OTHER` is
// supported alongside a free-text `custom_grading_company` field on the
// listing. `API_GRADERS` is the narrower set that has certificate-lookup
// adapters wired up (see grading/adapters.server.ts).

export const ALL_GRADERS = [
  "RAW",
  "PSA",
  "BGS",
  "CGC",
  "ACE",
  "PG",
  "SGC",
  "TAG",
  "AGS",
  "ISA",
  "OTHER",
] as const;
export type Grader = (typeof ALL_GRADERS)[number];

/** Preserve casing-insensitive lookups against legacy rows. */
export function isGrader(v: string): v is Grader {
  return (ALL_GRADERS as readonly string[]).includes(v.toUpperCase());
}

/**
 * Grading companies offered in the certificate-lookup selector, in display
 * order. Every entry is selectable; only those in `VERIFIABLE_GRADERS` have a
 * provider integration wired up — the rest disable verification gracefully.
 */
export const API_GRADERS = ["psa", "bgs", "cgc", "sgc", "ace", "tag"] as const;
export type ApiGrader = (typeof API_GRADERS)[number];

/** Subset of `API_GRADERS` that currently has a verification provider adapter. */
export const VERIFIABLE_GRADERS = ["psa", "bgs", "cgc", "sgc"] as const;
export type VerifiableGrader = (typeof VERIFIABLE_GRADERS)[number];

export function isApiGrader(v: string | null | undefined): boolean {
  return !!v && (API_GRADERS as readonly string[]).includes(v.toLowerCase());
}

export function supportsCertVerification(v: string | null | undefined): boolean {
  return !!v && (VERIFIABLE_GRADERS as readonly string[]).includes(v.toLowerCase());
}

/** Graders verified against the grading company's own public certificate record. */
export const EXTERNAL_VERIFIABLE_GRADERS = ["ace", "tag"] as const;
export type ExternalVerifiableGrader = (typeof EXTERNAL_VERIFIABLE_GRADERS)[number];

export function usesExternalVerification(
  v: string | null | undefined,
): v is ExternalVerifiableGrader {
  return !!v && (EXTERNAL_VERIFIABLE_GRADERS as readonly string[]).includes(v.toLowerCase());
}

export const CERT_VERIFICATION_UNAVAILABLE_MESSAGE =
  "Certificate verification is not yet available for this grading company.";

export function normaliseGraderCode(v: string | null | undefined): Grader | null {
  if (!v) return null;
  const s = v.trim().toUpperCase();
  if ((ALL_GRADERS as readonly string[]).includes(s)) return s as Grader;
  return null;
}

/** Full human labels shown in dropdowns and on listing cards. */
export const GRADER_LABEL: Record<Grader, string> = {
  RAW: "Raw / Ungraded",
  PSA: "PSA (Professional Sports Authenticator)",
  BGS: "Beckett (BGS)",
  CGC: "CGC Cards",
  ACE: "ACE Grading",
  PG: "PG Grading",
  SGC: "SGC",
  TAG: "TAG Grading",
  AGS: "AGS",
  ISA: "ISA Grading",
  OTHER: "Other",
};

/** Short label for compact places (cards, badges). */
export const GRADER_SHORT: Record<Grader, string> = {
  RAW: "Raw / Ungraded",
  PSA: "PSA",
  BGS: "BGS",
  CGC: "CGC",
  ACE: "ACE",
  PG: "PG Grading",
  SGC: "SGC",
  TAG: "TAG",
  AGS: "AGS",
  ISA: "ISA",
  OTHER: "Other",
};

/** For display-only mapping of legacy / unrecognised values. Never rewrites the row. */
export function displayGrader(raw: string | null | undefined): Grader {
  const g = normaliseGraderCode(raw);
  return g ?? "OTHER";
}

export const GRADER_ENV_KEY: Record<ApiGrader, string> = {
  psa: "PSA_API_TOKEN",
  bgs: "BGS_API_TOKEN",
  cgc: "CGC_API_TOKEN",
  sgc: "SGC_API_TOKEN",
  ace: "ACE_API_TOKEN",
  tag: "TAG_API_TOKEN",
};

// ---------------------------------------------------------------------------
// Grade values.
// Stored and displayed as text so 9.5 never rounds to 10 and legacy values
// survive unchanged.
// ---------------------------------------------------------------------------

export const NUMERIC_GRADES = [
  "1",
  "1.5",
  "2",
  "2.5",
  "3",
  "3.5",
  "4",
  "4.5",
  "5",
  "5.5",
  "6",
  "6.5",
  "7",
  "7.5",
  "8",
  "8.5",
  "9",
  "9.5",
  "10",
  "Pristine 10",
] as const;

/** Ordered for dropdowns: non-numeric first, then ascending numerics, then Other. */
export const GRADE_OPTIONS = ["Ungraded", "Authentic", ...NUMERIC_GRADES, "Other"] as const;
export type GradeOption = (typeof GRADE_OPTIONS)[number];

/**
 * Human descriptor per grade. Covers every stored numeric value so legacy rows
 * (e.g. "7.5", "2.5") still render with a friendly label even though the seller
 * dropdown only offers the curated list below.
 */
export const GRADE_DESCRIPTOR: Record<string, string> = {
  "Pristine 10": "Pristine",
  "10": "Gem Mint",
  "9.5": "Gem Mint",
  "9": "Mint",
  "8.5": "Near Mint / Mint+",
  "8": "Near Mint-Mint",
  "7.5": "Near Mint",
  "7": "Near Mint",
  "6.5": "Excellent-Mint",
  "6": "Excellent-Mint",
  "5.5": "Excellent",
  "5": "Excellent",
  "4.5": "Very Good-Excellent",
  "4": "Very Good-Excellent",
  "3.5": "Very Good",
  "3": "Very Good",
  "2.5": "Good",
  "2": "Good",
  "1.5": "Poor",
  "1": "Poor",
};

/** Dropdown label — "10 Gem Mint", "9.5 Gem Mint", "Pristine 10". */
export function formatGradeLabel(grade: string | null | undefined): string {
  if (!grade) return "";
  const g = grade.trim();
  const desc = GRADE_DESCRIPTOR[g];
  if (!desc) return g;
  if (g === "Pristine 10") return "Pristine 10";
  return `${g} ${desc}`;
}

/** Badge label — "PRISTINE 10", "GEM MINT 10", "GEM MINT 9.5". Uppercase, descriptor first. */
export function formatGradeBadge(grade: string | null | undefined): string {
  if (!grade) return "";
  const g = grade.trim();
  const desc = GRADE_DESCRIPTOR[g];
  if (!desc) return g.toUpperCase();
  if (g === "Pristine 10") return "PRISTINE 10";
  return `${desc.toUpperCase()} ${g}`;
}

/** Colour tier for a grade badge — used to tint premium GradeBadge chips. */
export function gradeBadgeTier(
  grade: string | null | undefined,
): "pristine" | "gem" | "mint" | "near-mint" | "excellent" | "good" | "poor" | "none" {
  if (!grade) return "none";
  const g = grade.trim();
  if (g === "Pristine 10") return "pristine";
  const n = Number(g);
  if (!Number.isFinite(n)) return "none";
  if (n >= 9.5) return "gem";
  if (n >= 9) return "mint";
  if (n >= 7) return "near-mint";
  if (n >= 5) return "excellent";
  if (n >= 3) return "good";
  return "poor";
}

/** Filter-ordering helper: numerics sorted descending, non-numerics grouped. */
export function sortedGradeFilters(order: "asc" | "desc" = "desc"): string[] {
  const nums = [...NUMERIC_GRADES];
  if (order === "desc") nums.reverse();
  return ["Ungraded", "Authentic", ...nums, "Other"];
}

// ---------------------------------------------------------------------------
// Card type (per-listing sub-classification within Trading Cards).
// ---------------------------------------------------------------------------

export const CARD_TYPES = ["raw", "graded", "sealed"] as const;
export type CardType = (typeof CARD_TYPES)[number];

export const CARD_TYPE_LABEL: Record<CardType, string> = {
  raw: "Raw card",
  graded: "Graded card",
  sealed: "Sealed product",
};

export function normaliseCardType(v: string | null | undefined): CardType | null {
  if (!v) return null;
  const s = v.trim().toLowerCase();
  if ((CARD_TYPES as readonly string[]).includes(s)) return s as CardType;
  return null;
}

// ---------------------------------------------------------------------------
// Product type — top-level marketplace classification.
// Trading Card, Sealed Product (booster boxes/packs/decks/tins) or Accessory.
// ---------------------------------------------------------------------------

export const PRODUCT_TYPES = ["trading_card", "sealed_product", "accessory", "collectible", "fashion"] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const PRODUCT_TYPE_LABEL: Record<ProductType, string> = {
  trading_card: "Trading Card",
  sealed_product: "Sealed Product",
  accessory: "Accessory",
  collectible: "Collectible / Figure",
  fashion: "Fashion / Clothing",
};

export function normaliseProductType(v: string | null | undefined): ProductType {
  if (!v) return "trading_card";
  const s = v.trim().toLowerCase();
  if ((PRODUCT_TYPES as readonly string[]).includes(s)) return s as ProductType;
  return "trading_card";
}

// ---------------------------------------------------------------------------
// Accessory categories.
// ---------------------------------------------------------------------------

export const ACCESSORY_CATEGORIES = [
  "sleeves",
  "penny_sleeves",
  "perfect_fit",
  "top_loaders",
  "magnetic_holders",
  "one_touch",
  "card_savers",
  "screwdown",
  "binders",
  "binder_pages",
  "deck_boxes",
  "storage_boxes",
  "playmats",
  "display_cases",
  "team_bags",
  "toploader_bags",
  "card_stands",
  "dice_counters",
  "supplies_bundle",
  "other",
] as const;
export type AccessoryCategory = (typeof ACCESSORY_CATEGORIES)[number];

export const ACCESSORY_CATEGORY_LABEL: Record<AccessoryCategory, string> = {
  sleeves: "Sleeves",
  penny_sleeves: "Penny Sleeves",
  perfect_fit: "Perfect Fit Sleeves",
  top_loaders: "Top Loaders",
  magnetic_holders: "Magnetic Holders",
  one_touch: "One-Touch Holders",
  card_savers: "Card Savers",
  screwdown: "Screwdown Holders",
  binders: "Binders",
  binder_pages: "Binder Pages",
  deck_boxes: "Deck Boxes",
  storage_boxes: "Storage Boxes",
  playmats: "Playmats",
  display_cases: "Display Cases",
  team_bags: "Team Bags",
  toploader_bags: "Toploader Bags",
  card_stands: "Card Stands",
  dice_counters: "Dice & Counters",
  supplies_bundle: "Supplies Bundle",
  other: "Other Accessory",
};

export function normaliseAccessoryCategory(v: string | null | undefined): AccessoryCategory | null {
  if (!v) return null;
  const s = v.trim().toLowerCase();
  if ((ACCESSORY_CATEGORIES as readonly string[]).includes(s)) return s as AccessoryCategory;
  return null;
}

// ---------------------------------------------------------------------------
// Display helper — single source of truth for grading / product badges.
// ---------------------------------------------------------------------------

export type GradingDisplayInput = {
  product_type?: string | null;
  accessory_category?: string | null;
  card_type?: string | null;
  grading_company?: string | null;
  custom_grading_company?: string | null;
  grade?: string | null;
  custom_grade?: string | null;
};

/**
 * Human-readable badge for grading / product state. Never returns
 * "Ungraded Ungraded" or empty strings. Used by auction cards, detail
 * pages, watchlist, etc.
 */
export function formatGradingBadge(a: GradingDisplayInput): string {
  const product = normaliseProductType(a.product_type);
  if (product === "accessory") {
    const cat = normaliseAccessoryCategory(a.accessory_category);
    return cat ? ACCESSORY_CATEGORY_LABEL[cat] : "Accessory";
  }
  if (product === "sealed_product") return "Sealed Product";

  const type = normaliseCardType(a.card_type);
  if (type === "sealed") return "Sealed Product";

  const raw = (a.grading_company ?? "").trim().toUpperCase();
  const grade = (a.grade ?? "").trim();

  // Raw / ungraded — either explicit code, "RAW", or empty.
  if (!raw || raw === "RAW" || type === "raw") return "Raw / Ungraded";

  if (raw === "OTHER") {
    const co = (a.custom_grading_company ?? "").trim();
    const gr = grade === "Other" ? (a.custom_grade ?? "").trim() : grade;
    const parts = ["Other"];
    if (co) parts.push(`— ${co}`);
    if (gr && gr.toLowerCase() !== "ungraded") parts.push(gr);
    return parts.join(" ").replace(/\s+/g, " ").trim();
  }

  const known = isGrader(raw);
  const label = known ? GRADER_SHORT[raw as Grader] : raw;
  const gradeText = grade === "Other" ? (a.custom_grade ?? "").trim() : formatGradeLabel(grade);
  if (!gradeText || gradeText.toLowerCase() === "ungraded") return label;
  return `${label} ${gradeText}`;
}

// ---------------------------------------------------------------------------
// Product-type-specific condition options.
// The underlying storage column is a shared enum (`card_condition`) that has
// been extended with the values below. We surface a curated, non-overlapping
// list per product type so sellers only see relevant options.
// ---------------------------------------------------------------------------

// Curated seller dropdown. `gem_mint`, `good`, `fair`, `poor` remain valid
// enum values on legacy rows and continue to render via CONDITION_LABEL.
export const TRADING_CARD_CONDITIONS = [
  "pristine",
  "mint",
  "near_mint",
  "excellent",
  "lightly_played",
  "moderately_played",
  "heavily_played",
  "damaged",
] as const;

export const SEALED_CONDITIONS = [
  "factory_sealed",
  "brand_new",
  "new_opened",
  "like_new",
  "used",
] as const;

export const ACCESSORY_CONDITIONS = [
  "brand_new",
  "like_new",
  "excellent",
  "good",
  "fair",
  "used",
] as const;

export type ConditionValue =
  | (typeof TRADING_CARD_CONDITIONS)[number]
  | (typeof SEALED_CONDITIONS)[number]
  | (typeof ACCESSORY_CONDITIONS)[number]
  // legacy values preserved for existing rows
  | "gem_mint"
  | "good"
  | "fair"
  | "poor"
  | "light_played"
  | "played";

export const CONDITION_LABEL: Record<ConditionValue, string> = {
  pristine: "Pristine",
  gem_mint: "Gem Mint",
  mint: "Mint",
  near_mint: "Near Mint",
  excellent: "Excellent",
  lightly_played: "Lightly Played",
  moderately_played: "Moderately Played",
  heavily_played: "Heavily Played",
  damaged: "Damaged",
  factory_sealed: "Factory Sealed",
  brand_new: "Brand New",
  new_opened: "New – Opened but Unused",
  like_new: "Like New",
  good: "Good",
  fair: "Fair",
  used: "Used",
  light_played: "Lightly Played",
  played: "Played",
  poor: "Poor",
};

export function conditionsForProductType(product: ProductType): readonly string[] {
  if (product === "sealed_product") return SEALED_CONDITIONS;
  if (product === "accessory" || product === "collectible" || product === "fashion") return ACCESSORY_CONDITIONS;
  return TRADING_CARD_CONDITIONS;
}

export function defaultConditionFor(product: ProductType): string {
  if (product === "sealed_product") return "factory_sealed";
  if (product === "accessory" || product === "collectible" || product === "fashion") return "brand_new";
  return "near_mint";
}

export function labelForCondition(value: string | null | undefined): string {
  if (!value) return "";
  const key = value as ConditionValue;
  return CONDITION_LABEL[key] ?? value.replace(/_/g, " ");
}

// ---------------------------------------------------------------------------
// Compatible-game presets for accessories (optional).
// Stored as free text; "Other" allows a custom typed value.
// ---------------------------------------------------------------------------

export const COMPATIBLE_GAMES = [
  "Pokémon",
  "One Piece",
  "Yu-Gi-Oh!",
  "Magic: The Gathering",
  "Dragon Ball Super",
  "Lorcana",
  "Flesh and Blood",
  "Digimon",
  "Sports Cards",
  "Universal",
  "Other",
] as const;

// ---------------------------------------------------------------------------
// Trading Card professional attributes: language, finish, edition.
// All optional — legacy rows without these values render fine everywhere.
// ---------------------------------------------------------------------------

export const CARD_LANGUAGES = [
  "English",
  "Japanese",
  "Traditional Chinese",
  "Simplified Chinese",
  "Korean",
  "French",
  "German",
  "Italian",
  "Spanish",
  "Portuguese",
  "Thai",
  "Indonesian",
  "Other",
] as const;
export type CardLanguage = (typeof CARD_LANGUAGES)[number];

/** Flag emoji shown next to the language on badges. */
export const CARD_LANGUAGE_FLAG: Record<CardLanguage, string> = {
  English: "🇬🇧",
  Japanese: "🇯🇵",
  "Traditional Chinese": "🇹🇼",
  "Simplified Chinese": "🇨🇳",
  Korean: "🇰🇷",
  French: "🇫🇷",
  German: "🇩🇪",
  Italian: "🇮🇹",
  Spanish: "🇪🇸",
  Portuguese: "🇵🇹",
  Thai: "🇹🇭",
  Indonesian: "🇮🇩",
  Other: "🏳️",
};

export function normaliseCardLanguage(v: string | null | undefined): CardLanguage | null {
  if (!v) return null;
  const s = v.trim();
  const hit = CARD_LANGUAGES.find((x) => x.toLowerCase() === s.toLowerCase());
  return hit ?? null;
}

export function formatLanguageBadge(v: string | null | undefined): string | null {
  if (!v) return null;
  const lang = normaliseCardLanguage(v);
  if (!lang) return v;
  return `${CARD_LANGUAGE_FLAG[lang]} ${lang}`;
}

export const FINISH_OPTIONS = [
  "Non Holo",
  "Holo",
  "Reverse Holo",
  "Full Art",
  "Alternate Art",
  "Illustration Rare (IR)",
  "Special Illustration Rare (SIR)",
  "Art Rare (AR)",
  "Secret Rare (SR)",
  "Super Rare (SR)",
  "Ultra Rare (UR)",
  "Hyper Rare",
  "Rainbow Rare",
  "Gold Rare",
  "Promo",
  "Master Ball Reverse",
  "Poké Ball Reverse",
  "Other",
] as const;
export type FinishOption = (typeof FINISH_OPTIONS)[number];

export const EDITION_OPTIONS = [
  "Unlimited",
  "1st Edition",
  "Unlimited (Japanese)",
  "Limited",
  "Promo",
  "Other",
] as const;
export type EditionOption = (typeof EDITION_OPTIONS)[number];

/**
 * Grading company options exposed on the seller listing form. Narrower than
 * ALL_GRADERS (which also includes legacy codes such as PG/TAG/AGS/ISA/OTHER
 * that we still render on old rows).
 */
// Order shown in the seller listing form. Verification-capable graders first
// (PSA / BGS / CGC / SGC — see API_GRADERS), followed by manual-only graders,
// then OTHER which reveals a free-text custom grading company field.
export const SELLER_GRADING_COMPANIES = [
  "PSA",
  "BGS",
  "CGC",
  "SGC",
  "ACE",
  "TAG",
  "PG",
  "OTHER",
] as const;
export type SellerGradingCompany = (typeof SELLER_GRADING_COMPANIES)[number];

/**
 * Grade values offered on the seller listing form. Storage stays free-text
 * so legacy half-step grades on old rows still render unchanged.
 */
export const SELLER_GRADE_OPTIONS = [
  "Pristine 10",
  "10",
  "9.5",
  "9",
  "8.5",
  "8",
  "7",
  "6",
  "5",
  "4",
  "3",
  "2",
  "1",
  "Ungraded",
] as const;
export type SellerGradeOption = (typeof SELLER_GRADE_OPTIONS)[number];

// ---------------------------------------------------------------------------
// Trading Card Game / franchise. Machine values snake_case; labels human-readable.
// ---------------------------------------------------------------------------

export const TCG_GAMES = [
  { value: "pokemon", label: "Pokémon" },
  { value: "one_piece", label: "One Piece" },
  { value: "yu_gi_oh", label: "Yu-Gi-Oh!" },
  { value: "magic_the_gathering", label: "Magic: The Gathering" },
  { value: "disney_lorcana", label: "Disney Lorcana" },
  { value: "dragon_ball", label: "Dragon Ball" },
  { value: "digimon", label: "Digimon" },
  { value: "weiss_schwarz", label: "Weiss Schwarz" },
  { value: "union_arena", label: "Union Arena" },
  { value: "flesh_and_blood", label: "Flesh and Blood" },
  { value: "sports_cards", label: "Sports Cards" },
  { value: "other", label: "Other" },
] as const;
export type TcgGameValue = (typeof TCG_GAMES)[number]["value"];

export const TCG_GAME_LABEL: Record<string, string> = Object.fromEntries(
  TCG_GAMES.map((g) => [g.value, g.label]),
);
export function labelForTcgGame(v: string | null | undefined): string | null {
  if (!v) return null;
  return TCG_GAME_LABEL[v] ?? v;
}

// Rarity. Kept separate from Finish. Optional field.
export const RARITY_OPTIONS = [
  { value: "common", label: "Common" },
  { value: "uncommon", label: "Uncommon" },
  { value: "rare", label: "Rare" },
  { value: "double_rare", label: "Double Rare" },
  { value: "triple_rare", label: "Triple Rare" },
  { value: "art_rare", label: "Art Rare" },
  { value: "illustration_rare", label: "Illustration Rare" },
  { value: "special_art_rare", label: "Special Art Rare" },
  { value: "special_illustration_rare", label: "Special Illustration Rare" },
  { value: "secret_rare", label: "Secret Rare" },
  { value: "super_rare", label: "Super Rare" },
  { value: "ultra_rare", label: "Ultra Rare" },
  { value: "hyper_rare", label: "Hyper Rare" },
  { value: "gold_rare", label: "Gold Rare" },
  { value: "rainbow_rare", label: "Rainbow Rare" },
  { value: "promo", label: "Promo" },
  { value: "other", label: "Other" },
] as const;
export const RARITY_LABEL: Record<string, string> = Object.fromEntries(
  RARITY_OPTIONS.map((r) => [r.value, r.label]),
);
export function labelForRarity(v: string | null | undefined): string | null {
  if (!v) return null;
  return RARITY_LABEL[v] ?? v;
}

// Grading status — graded vs raw. Mirrors existing card_type (graded/raw) for
// trading cards; new column lets us filter/validate without disturbing legacy
// card_type usage (which also has 'sealed').
export const GRADING_STATUS_OPTIONS = [
  { value: "graded", label: "Graded" },
  { value: "raw", label: "Raw / Ungraded" },
] as const;
export type GradingStatus = "graded" | "raw";

/** Short 2-letter language code for compact card badges. */
export const CARD_LANGUAGE_CODE: Record<CardLanguage, string> = {
  English: "EN",
  Japanese: "JP",
  "Traditional Chinese": "TC",
  "Simplified Chinese": "SC",
  Korean: "KR",
  French: "FR",
  German: "DE",
  Italian: "IT",
  Spanish: "ES",
  Portuguese: "PT",
  Thai: "TH",
  Indonesian: "ID",
  Other: "—",
};
export function shortLanguageCode(v: string | null | undefined): string | null {
  if (!v) return null;
  const lang = normaliseCardLanguage(v);
  return lang ? CARD_LANGUAGE_CODE[lang] : null;
}
